import React from 'react';
import { bool, node, object, string } from 'prop-types';
import classNames from 'classnames';

import { ExternalLink, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './StaticContentPage.module.css';

const renderParagraphs = paragraphs =>
  (paragraphs || []).map(paragraph => <p key={paragraph}>{paragraph}</p>);

const renderList = (items, ordered = false) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag className={css.list}>
      {items.map(item => (
        <li key={item}>{item}</li>
      ))}
    </ListTag>
  );
};

const TableMaybe = ({ table }) => {
  if (!table?.headers?.length || !table?.rows?.length) {
    return null;
  }

  return (
    <div className={css.tableScroll}>
      <table className={css.table}>
        <thead>
          <tr>
            {table.headers.map(header => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map(row => (
            <tr key={row.join('-')}>
              {row.map(cell => (
                <td key={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

TableMaybe.propTypes = {
  table: object,
};

export const StaticContentArticle = ({ content, headingLevel = 'h1', isEmbedded = false }) => {
  const TitleTag = headingLevel;
  const articleClassName = classNames(css.article, {
    [css.articleEmbedded]: isEmbedded,
  });

  return (
    <article className={articleClassName}>
      <header className={css.header}>
        <TitleTag className={css.title}>{content.title}</TitleTag>
        {content.subtitle ? <p className={css.subtitle}>{content.subtitle}</p> : null}
        {content.effectiveDate ? (
          <p className={css.effectiveDate}>
            <strong>Effective Date:</strong> {content.effectiveDate}
          </p>
        ) : null}
        {(content.introduction || []).length > 0 ? (
          <div className={css.intro}>{renderParagraphs(content.introduction)}</div>
        ) : null}
      </header>

      {(content.sections || []).map(section => {
        const {
          heading,
          paragraphs = [],
          listItems = [],
          orderedListItems = [],
          subsections = [],
          closingParagraphs = [],
          contact,
          table,
        } = section;

        return (
          <section key={heading} className={css.section}>
            <h2 className={css.sectionTitle}>{heading}</h2>

            {renderParagraphs(paragraphs)}
            {renderList(listItems)}
            {renderList(orderedListItems, true)}
            <TableMaybe table={table} />

            {subsections.map(subsection => (
              <div key={subsection.title} className={css.subsection}>
                <h3 className={css.subsectionTitle}>{subsection.title}</h3>
                {renderParagraphs(subsection.paragraphs)}
                {renderList(subsection.listItems)}
                {renderList(subsection.orderedListItems, true)}
                <TableMaybe table={subsection.table} />
                {renderParagraphs(subsection.closingParagraphs)}
              </div>
            ))}

            {renderParagraphs(closingParagraphs)}

            {contact ? (
              <div className={css.contactBlock}>
                {contact.organization ? (
                  <p className={css.contactLine}>{contact.organization}</p>
                ) : null}
                {contact.email ? (
                  <p className={css.contactLine}>
                    Email:{' '}
                    <a className={css.link} href={`mailto:${contact.email}`}>
                      {contact.email}
                    </a>
                  </p>
                ) : null}
                {contact.websiteUrl ? (
                  <p className={css.contactLine}>
                    Website:{' '}
                    <ExternalLink href={contact.websiteUrl} className={css.link}>
                      {contact.websiteLabel || contact.websiteUrl}
                    </ExternalLink>
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })}
    </article>
  );
};

StaticContentArticle.propTypes = {
  content: object.isRequired,
  headingLevel: string,
  isEmbedded: bool,
};

export const StaticContentPageLayout = ({ content, scrollingDisabled, children }) => {
  const pageTitle = content.metaTitle || content.title;
  const pageDescription = content.metaDescription || content.title;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={pageDescription}
      title={pageTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: content.title,
        description: pageDescription,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageContent}>{children || <StaticContentArticle content={content} />}</div>
      </LayoutSingleColumn>
    </Page>
  );
};

StaticContentPageLayout.propTypes = {
  content: object.isRequired,
  scrollingDisabled: bool.isRequired,
  children: node,
};
